import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Plus, LogOut, Ban, Briefcase, ArrowLeft, MessageSquare, Save,
  KeyRound, Smartphone, Bell, FileText, FileImage, FileCheck2, Lock, Eye,
  Download, ChevronDown, AlertTriangle, ExternalLink,
} from "lucide-react";
import AdminShell, { Panel, Btn, StatusPill } from "@/components/admin/AdminShell";
import { PrimaryTabs } from "@/components/admin/SubTabs";
import DataTable from "@/components/admin/DataTable";
import { api, apiBaseUrl } from "@/lib/api";

const TABS = [
  { id: "profile", label: "Profile" },
  { id: "security", label: "Security" },
  { id: "kyc", label: "KYC" },
  { id: "permissions", label: "Permissions" },
  { id: "trades", label: "Trades" },
  { id: "wallets", label: "Wallets" },
  { id: "risk", label: "Risk" },
  { id: "activity", label: "Activity" },
];

export default function UserDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [tab, setTab] = useState("profile");
  const [data, setData] = useState(null);
  const [showNote, setShowNote] = useState(false);
  const [err, setErr] = useState("");

  async function load() {
    try {
      const { data: d } = await api.get(`/api/admin/users/${id}`);
      setData(d);
    } catch (e) { setErr("User not found"); }
  }
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  if (!data) {
    return (
      <AdminShell title="User">
        <div className="text-muted-foreground">{err || "Loading…"}</div>
        <Btn kind="ghost" onClick={() => nav("/admin/users")} className="mt-4"><ArrowLeft size={13} /> Back</Btn>
      </AdminShell>
    );
  }

  const u = data.user;
  const initial = (u.full_name || u.email || "?").charAt(0).toUpperCase();

  async function patch(payload) {
    await api.patch(`/api/admin/users/${id}`, payload);
    load();
  }
  async function forceLogout() {
    if (!window.confirm("Force-logout all sessions for this user?")) return;
    await api.post(`/api/admin/users/${id}/force-logout`);
    load();
  }

  return (
    <AdminShell title="User">
      {/* Back */}
      <button onClick={() => nav("/admin/users")} className="mb-4 inline-flex items-center gap-1.5 text-[12px] text-muted-foreground hover:text-foreground"><ArrowLeft size={12} /> Back to users</button>

      {/* Header card */}
      <Panel className="p-5 mb-5" data-testid="user-detail-header">
        <div className="flex flex-wrap items-start gap-5">
          <div className="w-16 h-16 rounded-full brand-gradient text-primary-foreground grid place-items-center font-semibold text-[24px] shrink-0">{initial}</div>
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-1">
              <h2 className="text-[20px] font-semibold">{u.full_name || u.email}</h2>
              <span className="text-[11px] font-mono text-muted-foreground">UID: {u.id.slice(-8).toUpperCase()}</span>
              <StatusPill value={u.status || "active"} />
              <StatusPill value={u.kyc_status} />
            </div>
            <div className="text-[12px] text-muted-foreground">{u.email} {u.mobile_number && <span> · {u.mobile_number}</span>}</div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Btn kind="ghost" onClick={() => setShowNote(true)} data-testid="add-note"><Plus size={13} /> Add Note</Btn>
            <Btn kind="ghost" onClick={forceLogout} data-testid="force-logout"><LogOut size={13} /> Force Logout</Btn>
            <Btn kind="ghost" onClick={() => patch({ status: u.status === "restricted" ? "active" : "restricted" })} data-testid="restrict"><Ban size={13} /> {u.status === "restricted" ? "Un-restrict" : "Restrict"} User</Btn>
            <Btn kind="primary" onClick={() => {
              const next = window.prompt("Account type (T1/T2/T3/VIP1/VIP2/VIP3):", u.tier || "T1");
              if (next) patch({ tier: next });
            }} data-testid="change-account-type"><Briefcase size={13} /> Change Account Type</Btn>
          </div>
        </div>
      </Panel>

      <PrimaryTabs tabs={TABS} value={tab} onChange={setTab} testIdPrefix="user-tab" />
      <div className="mt-5">
        {tab === "profile" && <ProfileTab user={u} onPatch={patch} reload={load} />}
        {tab === "security" && <SecurityTab user={u} sessions={data.sessions} onForceLogout={forceLogout} />}
        {tab === "kyc" && <KycTab userId={id} user={u} />}
        {tab === "permissions" && <PermissionsTab userId={id} />}
        {tab === "trades" && <TradesTab userId={id} />}
        {tab === "wallets" && <WalletsTab userId={id} />}
        {tab === "risk" && <RiskTab user={u} />}
        {tab === "activity" && <ActivityTab history={data.login_history} />}
      </div>

      {showNote && <NoteModal userId={id} onClose={() => setShowNote(false)} onSaved={() => { setShowNote(false); load(); }} />}
    </AdminShell>
  );
}

// ---------------------------------------------------------------------------
function ProfileTab({ user, onPatch, reload }) {
  const fields = [
    ["Full Name", user.full_name || "—"],
    ["Email", user.email],
    ["Phone", user.mobile_number || "—"],
    ["User ID", user.id],
    ["Account Type", <select key="tier" value={user.tier || "T1"} onChange={(e) => onPatch({ tier: e.target.value })} className="bg-background border border-line rounded-md px-2 py-1 text-[12px]">{["T1","T2","T3","VIP1","VIP2","VIP3"].map((s) => <option key={s}>{s}</option>)}</select>],
    ["VIP Level", user.tier?.startsWith("VIP") ? user.tier : "—"],
    ["Country", user.country || "—"],
    ["Registration Source", "Web (Direct)"],
    ["Referral Code", user.referral_code || "—"],
    ["Joined", user.created_at ? new Date(user.created_at).toLocaleDateString() : "—"],
    ["Last Updated", user.updated_at ? new Date(user.updated_at).toLocaleDateString() : "—"],
  ];
  return (
    <div className="grid lg:grid-cols-[1fr_360px] gap-4">
      <Panel className="p-5">
        <h3 className="text-[14px] font-semibold mb-4">User Profile</h3>
        <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-[13px]">
          {fields.map(([k, v]) => (
            <div key={k}>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{k}</div>
              <div className="font-semibold">{v}</div>
            </div>
          ))}
        </div>
      </Panel>

      <Panel className="p-5">
        <h3 className="text-[14px] font-semibold mb-3 flex items-center gap-2"><MessageSquare size={14} /> Internal Notes</h3>
        {!user.internal_notes || user.internal_notes.length === 0 ? (
          <div className="text-[12px] text-muted-foreground">No notes yet.</div>
        ) : (
          <div className="space-y-2 max-h-[280px] overflow-y-auto">
            {user.internal_notes.slice().reverse().map((n, i) => (
              <div key={i} className="p-3 bg-background border border-line rounded-md">
                <div className="text-[10px] text-muted-foreground mb-1">{n.by} · {new Date(n.at).toLocaleString()}</div>
                <div className="text-[12px]">{n.body}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

function SecurityTab({ user, sessions, onForceLogout }) {
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Panel className="p-5">
        <h3 className="text-[14px] font-semibold mb-4">Authentication</h3>
        <div className="space-y-3">
          <Row icon={Smartphone} label="Two-Factor Authentication" value={user.totp_enabled ? "Enabled" : "Not enabled"} />
          <Row icon={KeyRound} label="Email Verified" value={user.email_verified ? "Yes" : "No"} />
          <Row icon={KeyRound} label="Mobile Verified" value={user.mobile_verified ? "Yes" : "No"} />
          <Row icon={Bell} label="Login alerts" value="Enabled" />
        </div>
      </Panel>
      <Panel className="p-5">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-[14px] font-semibold">Active Sessions ({sessions.length})</h3>
          {sessions.length > 0 && <Btn kind="danger" onClick={onForceLogout}><LogOut size={12} /> Terminate all</Btn>}
        </div>
        {sessions.length === 0 ? <div className="text-[12px] text-muted-foreground">No active sessions.</div> : (
          <div className="space-y-2 max-h-[260px] overflow-y-auto">
            {sessions.map((s) => (
              <div key={s.id} className="p-3 bg-background border border-line rounded-md text-[12px]">
                <div className="font-mono">{s.ip || "Unknown IP"}</div>
                <div className="text-[10px] text-muted-foreground">{new Date(s.created_at).toLocaleString()}</div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

function KycTab({ userId, user }) {
  const [kyc, setKyc] = useState(null);
  const [showStatus, setShowStatus] = useState(false);
  const [showDocs, setShowDocs] = useState(false);
  const [digioKid, setDigioKid] = useState("");
  const [digioBusy, setDigioBusy] = useState(false);
  const [digioErr, setDigioErr] = useState("");
  const [digioWarn, setDigioWarn] = useState("");
  const reload = () => api.get(`/api/admin/users/${userId}/kyc`).then(({ data }) => setKyc(data));
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [userId]);
  useEffect(() => {
    if (kyc?.digio?.kid) setDigioKid(kyc.digio.kid);
  }, [kyc?.digio?.kid]);
  if (!kyc) return <Panel className="p-5 text-muted-foreground">Loading…</Panel>;

  const statusColor = {
    approved: ["#44D15B", "rgba(16,185,129,0.18)"],
    verified: ["#44D15B", "rgba(16,185,129,0.18)"],
    completed: ["#44D15B", "rgba(16,185,129,0.18)"],
    pending:  ["#28CA71", "rgba(40, 202, 113,0.18)"],
    pending_review: ["#28CA71", "rgba(40, 202, 113,0.18)"],
    rejected: ["#F4455D", "rgba(239,68,68,0.18)"],
    not_submitted: ["#899CA3", "rgba(136,146,168,0.18)"],
  };
  const SP = ({ value }) => {
    const [fg, bg] = statusColor[value] || ["#899CA3", "rgba(136,146,168,0.18)"];
    return <span className="text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded" style={{ color: fg, background: bg }}>{(value || "—").replace("_", " ")}</span>;
  };

  const fetchDigio = async () => {
    setDigioBusy(true);
    setDigioErr("");
    setDigioWarn("");
    try {
      const payload = digioKid.trim() ? { kid: digioKid.trim() } : {};
      const { data } = await api.post(`/api/admin/users/${userId}/kyc/digio/sync`, payload);
      if (data?.warning) setDigioWarn(data.warning);
      if (data?.kyc) setKyc(data.kyc);
      else await reload();
    } catch (e) {
      setDigioErr(e?.response?.data?.detail || e?.message || "Failed to fetch Digio details");
    } finally {
      setDigioBusy(false);
    }
  };

  const digio = kyc.digio || null;
  const parsed = digio?.parsed || {};
  const truid = kyc.truid || null;
  const isTruid = Boolean(
    truid
    && (truid.verification_mode === "truid" || truid.truid_status || truid.aadhaar)
  );
  const isDigio = Boolean(digio && (digio.kid || digio.status || digio.digio_status));
  const isSignzy = kyc.signzy?.verification_mode === "signzy";
  const addrText = formatTruidAddress(truid?.address);
  const panVerify = truid?.pan_verify;
  const panNumber =
    truid?.pan_number
    || truid?.pan?.pan_number
    || truid?.pan_info?.number
    || panVerify?.pan
    || "—";

  return (
    <div className="space-y-5">
      <Panel className="p-5">
        <div className="flex items-start justify-between mb-5 flex-wrap gap-3">
          <div>
            <h3 className="text-[16px] font-semibold">KYC</h3>
            <p className="text-[12px] text-muted-foreground mt-1">
              {isTruid ? "TruID DigiLocker" : isDigio ? "Digio" : isSignzy ? "Signzy" : "Manual / documents"}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            {!isTruid && (
              <>
                <button onClick={fetchDigio} disabled={digioBusy} data-testid="kyc-fetch-digio"
                  className="bg-up text-white inline-flex items-center gap-1.5 h-[38px] px-4 rounded-md font-semibold text-[12px] disabled:opacity-60">
                  <Download size={13} /> {digioBusy ? "Fetching…" : "Fetch from Digio"}
                </button>
                <button onClick={() => setShowDocs(true)} data-testid="kyc-update-docs"
                  className="inline-flex items-center gap-1.5 h-[38px] px-4 rounded-md border border-border bg-surface-2 font-semibold text-[12px]">
                  <FileImage size={13} /> Update Documents
                </button>
              </>
            )}
            <button onClick={() => setShowStatus(true)} data-testid="kyc-update-status"
              className="brand-gradient text-primary-foreground inline-flex items-center gap-1.5 h-[38px] px-4 rounded-md font-semibold text-[12px]">
              <FileCheck2 size={13} /> Update KYC Status
            </button>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <KycKpi label="KYC Tier" value={<SP value={(kyc.kyc_tier || "tier_1").toLowerCase().replace(" ", "_")} />} altLabel={kyc.kyc_tier} />
          <KycKpi label="Account status" value={<SP value={kyc.status} />} />
          <KycKpi label="Country" value={<span className="text-[16px] font-semibold">{kyc.country || "IN"}</span>} />
          {isTruid ? (
            <KycKpi label="TruID step" value={<span className="text-[14px] font-semibold">{(truid.truid_status || "—").replace(/_/g, " ")}</span>} />
          ) : (
            <KycKpi label="ID Type Required" value={<span className="text-[15px] font-semibold">{kyc.id_type_required}</span>} />
          )}
        </div>
      </Panel>

      {isTruid && (
        <Panel className="p-5">
          <h3 className="text-[14px] font-semibold mb-4 text-sky-300">TruID verification</h3>
          <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 text-[13px]">
            <KycKpi label="Name" value={<span className="text-[14px] font-semibold">{truid.full_name || kyc.full_name || "—"}</span>} />
            <KycKpi label="DOB" value={<span className="text-[14px] font-semibold">{truid.date_of_birth || "—"}</span>} />
            <KycKpi label="Gender" value={<span className="text-[14px] font-semibold">{truid.gender || "—"}</span>} />
            <KycKpi label="Aadhaar" value={<span className="text-[13px] font-semibold">{truid.aadhaar_number || truid.aadhaar?.aadhaar_number || "—"}</span>} />
            <KycKpi label="PAN" value={<span className="text-[13px] font-semibold">{panNumber}</span>} />
            {panVerify && (
              <KycKpi
                label="PAN Detailed"
                value={
                  <span className={`text-[13px] font-semibold ${panVerify.verified ? "text-emerald-400" : "text-red-400"}`}>
                    {panVerify.verified ? "Passed" : "Failed"}
                    {panVerify.registered_name ? ` · ${panVerify.registered_name}` : ""}
                  </span>
                }
              />
            )}
            {truid.face_match && (
              <KycKpi
                label="Face match"
                value={
                  <span className={`text-[14px] font-semibold ${truid.face_match.verified ? "text-emerald-400" : "text-red-400"}`}>
                    {truid.face_match.verified ? "Passed" : "Failed"}
                    {truid.face_match.match_percentage != null ? ` (${truid.face_match.match_percentage}%)` : ""}
                  </span>
                }
              />
            )}
          </div>
          {panVerify?.message && !panVerify.verified && (
            <div className="mt-3 text-[12px] text-red-300 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
              {panVerify.message}
            </div>
          )}
          {addrText && (
            <div className="mt-4 text-[12px] text-muted-foreground">
              <span className="uppercase tracking-wider text-[10px] block mb-1">Address</span>
              {addrText}
            </div>
          )}
          <div className="mt-4 grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Aadhaar photo</div>
              {truid.aadhaar_photo_url ? (
                <a href={mediaUrl(truid.aadhaar_photo_url)} target="_blank" rel="noreferrer">
                  <img
                    src={mediaUrl(truid.aadhaar_photo_url)}
                    alt="Aadhaar"
                    className="rounded-lg border border-line max-h-48 w-full object-contain bg-background"
                  />
                </a>
              ) : (
                <div className="rounded-lg border border-dashed border-line px-3 py-6 text-[12px] text-muted-foreground text-center">
                  {truid.photo_missing
                    ? "DigiLocker did not return an Aadhaar face photo."
                    : "No Aadhaar photo stored yet."}
                </div>
              )}
              {truid.aadhaar_pdf_url ? (
                <a
                  href={mediaUrl(truid.aadhaar_pdf_url)}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-2 inline-block text-[12px] text-sky-300 hover:underline"
                >
                  Open eAadhaar PDF
                </a>
              ) : null}
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Selfie</div>
              {truid.selfie_url ? (
                <a href={mediaUrl(truid.selfie_url)} target="_blank" rel="noreferrer">
                  <img
                    src={mediaUrl(truid.selfie_url)}
                    alt="Selfie"
                    className="rounded-lg border border-line max-h-48 w-full object-contain bg-background"
                  />
                </a>
              ) : (
                <div className="rounded-lg border border-dashed border-line px-3 py-6 text-[12px] text-muted-foreground text-center">
                  No live selfie yet.
                </div>
              )}
            </div>
          </div>
        </Panel>
      )}

      {!isTruid && (
        <Panel className="p-5">
          <h3 className="text-[14px] font-semibold mb-4">Indian KYC Documents</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <DocCard doc={kyc.documents.aadhaar} kind="aadhaar" SP={SP} />
            <DocCard doc={kyc.documents.pan} kind="pan" SP={SP} />
          </div>
        </Panel>
      )}

      {!isTruid && isDigio && (
        <Panel className="p-5">
          <div className="flex items-start justify-between mb-4 flex-wrap gap-3">
            <h3 className="text-[14px] font-semibold text-emerald-300">Digio verification</h3>
            <button onClick={fetchDigio} disabled={digioBusy} data-testid="kyc-fetch-digio-panel"
              className="inline-flex items-center gap-1.5 h-[34px] px-3 rounded-md font-semibold text-[11px] disabled:opacity-60"
              style={{ background: "rgba(16,185,129,0.2)", color: "#34D399", border: "1px solid rgba(16,185,129,0.35)" }}>
              <Download size={12} /> {digioBusy ? "Fetching…" : "Fetch details"}
            </button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <label className="block">
              <span className="block text-[11px] uppercase tracking-wider mb-2 text-muted-foreground">Digio KID (optional override)</span>
              <input
                value={digioKid}
                onChange={(e) => setDigioKid(e.target.value)}
                placeholder={digio.kid || "KID from Digio dashboard"}
                className="w-full h-[38px] px-3 rounded-md bg-background border text-[13px] font-mono"
                data-testid="kyc-digio-kid"
              />
            </label>
            <KycKpi label="Digio status" value={<SP value={digio.status || digio.digio_status || "—"} />} />
          </div>
          {digioErr && (
            <div className="mb-4 text-[12px] text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">
              {typeof digioErr === "string" ? digioErr : JSON.stringify(digioErr)}
            </div>
          )}
          {digioWarn && (
            <div className="mb-4 text-[12px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-md px-3 py-2">
              {digioWarn}
            </div>
          )}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 text-[13px]">
            <KycKpi label="KID" value={<span className="text-[11px] font-mono break-all">{digio.kid || "—"}</span>} />
            <KycKpi label="Customer" value={<span className="text-[13px] font-semibold">{digio.customerName || kyc.full_name || "—"}</span>} />
            <KycKpi label="PAN" value={<span className="text-[13px] font-mono font-semibold">{parsed.pan_number || "—"}</span>} />
            <KycKpi label="Aadhaar" value={<span className="text-[13px] font-mono font-semibold">{parsed.aadhaar_number || "—"}</span>} />
          </div>
          <DigioMediaGallery media={digio.media} />
        </Panel>
      )}

      {!isTruid && isSignzy && (
        <Panel className="p-5">
          <h3 className="text-[14px] font-semibold mb-4 text-emerald-300">Signzy verification</h3>
          <div className="grid grid-cols-2 gap-4 text-[13px]">
            <KycKpi label="Signzy status" value={<span className="text-[14px] font-semibold">{kyc.signzy.signzy_status || "—"}</span>} />
            <KycKpi label="Engine" value={<span className="text-[14px] font-semibold">{kyc.signzy.verification?.engine || "—"}</span>} />
            {kyc.signzy.face_match && (
              <KycKpi
                label="Face match"
                value={
                  <span className={`text-[14px] font-semibold ${kyc.signzy.face_match.verified ? "text-emerald-400" : "text-red-400"}`}>
                    {kyc.signzy.face_match.verified ? "Passed" : "Failed"}
                    {kyc.signzy.face_match.match_percentage != null ? ` (${kyc.signzy.face_match.match_percentage}%)` : ""}
                  </span>
                }
              />
            )}
            {kyc.signzy.pan_verify && (
              <KycKpi
                label="PAN verify"
                value={
                  <span className={`text-[14px] font-semibold ${kyc.signzy.pan_verify.verified ? "text-emerald-400" : "text-amber-400"}`}>
                    {kyc.signzy.pan_verify.verified ? "Passed" : "Failed"}
                  </span>
                }
              />
            )}
          </div>
        </Panel>
      )}

      {showStatus && (
        <KycStatusModal user={user} kyc={kyc} userId={userId}
          onClose={() => setShowStatus(false)}
          onSaved={() => { setShowStatus(false); reload(); }} />
      )}
      {showDocs && (
        <KycDocsModal kyc={kyc} userId={userId}
          onClose={() => setShowDocs(false)}
          onSaved={() => { setShowDocs(false); reload(); }} />
      )}
    </div>
  );
}

function formatTruidAddress(addr) {
  if (!addr) return "";
  if (typeof addr === "string") return addr;
  if (typeof addr !== "object") return "";
  return [
    addr.house, addr.street, addr.landmark, addr.locality, addr.vtc || addr.city,
    addr.district || addr.dist, addr.state, addr.pincode || addr.pc,
  ].filter(Boolean).join(", ");
}

function mediaUrl(url, dataUrl) {
  if (dataUrl && String(dataUrl).startsWith("data:")) return dataUrl;
  if (!url) return "";
  if (/^https?:\/\//i.test(url) || url.startsWith("data:")) return url;
  const path = url.startsWith("/") ? url : `/${url}`;
  if (path.startsWith("/uploads/")) {
    const base = apiBaseUrl();
    // CRA dev server does not serve API uploads — prefer API host / proxy target
    if (base && !/localhost|127\.0\.0\.1/i.test(base)) return `${base}${path}`;
    const proxy = (process.env.REACT_APP_PROXY_TARGET || "https://api.paisacx.io").replace(/\/$/, "");
    return `${proxy}${path}`;
  }
  const base = apiBaseUrl() || "";
  return `${base}${path}`;
}

function DigioMediaGallery({ media }) {
  const items = Array.isArray(media) ? media.filter((m) => m?.url || m?.dataUrl) : [];
  if (!items.length) {
    return (
      <div className="mt-4 text-[12px] text-dim">
        No Digio images/reports stored yet — click Fetch details to pull them.
      </div>
    );
  }
  return (
    <div className="mt-4">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Digio media</div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((m) => {
          const href = mediaUrl(m.url, m.dataUrl);
          const isPdf = (m.contentType || "").includes("pdf") || /\.pdf$/i.test(m.url || "");
          return (
            <div key={`${m.kind}-${m.url || m.dataUrl}`} className="rounded-lg border bg-background overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2 border-b text-[11px]">
                <span className="font-semibold text-muted-foreground">{m.label || m.kind}</span>
                <a href={href} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-up hover:underline">
                  <ExternalLink size={11} /> Open
                </a>
              </div>
              <div className="h-[180px] grid place-items-center bg-surface">
                {isPdf ? (
                  <a href={href} target="_blank" rel="noreferrer" className="text-center text-muted-foreground hover:text-surface-2">
                    <FileText size={28} className="mx-auto mb-2" />
                    <div className="text-[12px]">PDF / report</div>
                  </a>
                ) : (
                  <img src={href} alt={m.label || m.kind} className="w-full h-full object-contain" />
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function KycKpi({ label, value }) {
  return (
    <div className="rounded-lg border bg-background p-4">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">{label}</div>
      <div className="font-semibold">{value}</div>
    </div>
  );
}

function DocCard({ doc, kind, SP }) {
  const isAadhaar = kind === "aadhaar";
  const accent = isAadhaar ? "#28CA71" : "#3B82F6";
  return (
    <div className="rounded-lg border bg-background overflow-hidden">
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-2">
          <FileText size={16} style={{ color: accent }} />
          <span className="font-semibold">{doc.label}</span>
        </div>
        <SP value={doc.status} />
      </div>
      <div className="p-4 space-y-4">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">{isAadhaar ? "Aadhaar Number" : "PAN Number"}</div>
          <div className="font-mono text-[15px] font-semibold">{doc.number || "—"}</div>
        </div>
        {doc.verified_at && (
          <div>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Verified At</div>
            <div className="text-[14px] font-semibold" style={{ color: "#44D15B" }}>
              {new Date(doc.verified_at).toLocaleString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false })}
            </div>
          </div>
        )}
        {isAadhaar ? (
          <div className="grid grid-cols-2 gap-3">
            <DocImageBox label="Front Side" url={doc.front_url} dataUrl={doc.front_data_url} />
            <DocImageBox label="Back Side"  url={doc.back_url} dataUrl={doc.back_data_url} />
          </div>
        ) : (
          <DocImageBox label="PAN Card Image" url={doc.image_url} dataUrl={doc.image_data_url} large />
        )}
      </div>
    </div>
  );
}

function DocImageBox({ label, url, dataUrl, large }) {
  const href = mediaUrl(url, dataUrl);
  return (
    <div className={`rounded-lg border bg-surface grid place-items-center ${large ? "h-[260px]" : "h-[160px]"}`}
     >
      {href ? (
        <img src={href} alt={label} className="w-full h-full object-contain" />
      ) : (
        <div className="text-center text-dim">
          <FileImage size={28} className="mx-auto mb-2" />
          <div className="text-[12px]">{label}</div>
        </div>
      )}
    </div>
  );
}

function KycStatusModal({ user, kyc, userId, onClose, onSaved }) {
  const [status, setStatus] = useState(kyc.status);
  const [tier, setTier] = useState(kyc.kyc_tier);
  const [reason, setReason] = useState("");
  async function save(e) {
    e.preventDefault();
    await api.patch(`/api/admin/users/${userId}/kyc/status`, { status, tier, reason });
    onSaved();
  }
  return (
    <ModalShell title="Update KYC Status" onClose={onClose}>
      <form onSubmit={save} className="space-y-4">
        <SelField label="Status" v={status} on={setStatus} options={["pending", "approved", "rejected"]} testid="kyc-modal-status" />
        <SelField label="KYC Tier" v={tier} on={setTier} options={["Tier 1", "Tier 2", "Tier 3"]} testid="kyc-modal-tier" />
        <label className="block">
          <span className="block text-[11px] uppercase tracking-wider mb-2 text-muted-foreground">Reason (optional)</span>
          <textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
            className="w-full px-3 py-2 rounded-md border bg-background text-[13px] outline-none"
            />
        </label>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose}
            className="flex-1 h-[42px] rounded-md border font-semibold text-[13px]"
           >Cancel</button>
          <button type="submit" data-testid="kyc-modal-save"
      className="brand-gradient text-primary-foreground flex-1 h-[42px] rounded-md font-semibold text-[13px]"
      >Save</button>
        </div>
      </form>
    </ModalShell>
  );
}

function KycDocsModal({ kyc, userId, onClose, onSaved }) {
  const [f, setF] = useState({
    aadhaar_number: kyc.documents.aadhaar.number || "",
    aadhaar_status: kyc.documents.aadhaar.status,
    pan_number: kyc.documents.pan.number || "",
    pan_status: kyc.documents.pan.status,
  });
  async function save(e) {
    e.preventDefault();
    await api.patch(`/api/admin/users/${userId}/kyc/documents`, f);
    onSaved();
  }
  return (
    <ModalShell title="Update KYC Documents" onClose={onClose} maxWidth={520}>
      <form onSubmit={save} className="space-y-4">
        <h4 className="font-semibold text-[13px]" style={{ color: "#28CA71" }}>Aadhaar</h4>
        <TxtField label="Aadhaar Number" v={f.aadhaar_number} on={(v) => setF({ ...f, aadhaar_number: v })} />
        <SelField label="Aadhaar Status" v={f.aadhaar_status} on={(v) => setF({ ...f, aadhaar_status: v })}
          options={["not_submitted", "pending", "verified", "rejected"]} />
        <h4 className="font-semibold text-[13px] mt-2" style={{ color: "#3B82F6" }}>PAN</h4>
        <TxtField label="PAN Number" v={f.pan_number} on={(v) => setF({ ...f, pan_number: v })} />
        <SelField label="PAN Status" v={f.pan_status} on={(v) => setF({ ...f, pan_status: v })}
          options={["not_submitted", "pending", "verified", "rejected"]} />
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={onClose}
            className="flex-1 h-[42px] rounded-md border font-semibold text-[13px]"
           >Cancel</button>
          <button type="submit" data-testid="kyc-docs-save"
      className="brand-gradient text-primary-foreground flex-1 h-[42px] rounded-md font-semibold text-[13px]"
      >Save</button>
        </div>
      </form>
    </ModalShell>
  );
}

function ModalShell({ title, onClose, children, maxWidth = 480 }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-[100] grid place-items-center p-4 bg-black/70" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        className="w-full rounded-xl border bg-surface p-5"
        style={{ maxWidth, borderColor: "#1A2C34", color: "#E4EDEF" }}>
        <h3 className="text-[16px] font-semibold mb-4">{title}</h3>
        {children}
      </div>
    </div>
  );
}

function TxtField({ label, v, on }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider mb-2 text-muted-foreground">{label}</span>
      <input value={v} onChange={(e) => on(e.target.value)}
        className="w-full h-[40px] px-3 rounded-md border bg-background text-[13px] outline-none"
        />
    </label>
  );
}
function SelField({ label, v, on, options, testid }) {
  return (
    <label className="block">
      <span className="block text-[11px] uppercase tracking-wider mb-2 text-muted-foreground">{label}</span>
      <select value={v} onChange={(e) => on(e.target.value)} data-testid={testid}
        className="w-full h-[40px] px-3 rounded-md border bg-background text-[13px] outline-none capitalize"
       >
        {options.map((o) => <option key={o} value={o}>{o.replace("_", " ")}</option>)}
      </select>
    </label>
  );
}

function PermissionsTab({ userId }) {
  const [perms, setPerms] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get(`/api/admin/users/${userId}/permissions-v2`).then(({ data }) => setPerms(data));
  }, [userId]);
  async function save() {
    setSaving(true);
    await api.patch(`/api/admin/users/${userId}/permissions-v2`, perms);
    setSaving(false);
  }
  if (!perms) return <Panel className="p-5">Loading…</Panel>;
  const set = (path, val) => {
    const [s, k] = path.split(".");
    setPerms({ ...perms, [s]: { ...perms[s], [k]: val } });
  };

  return (
    <Panel className="p-5">
      <div className="flex items-center justify-between mb-5">
        <h3 className="text-[16px] font-semibold">User Permissions</h3>
        <button onClick={save} disabled={saving} data-testid="perms-save"
     className="brand-gradient text-primary-foreground inline-flex items-center gap-1.5 h-[38px] px-4 rounded-md font-semibold text-[12px]"
     >
          <Save size={13} /> {saving ? "Saving…" : "Save Changes"}
        </button>
      </div>

      {/* Trading */}
      <PermSection title="Trading Permissions">
        <PermToggle label="OTC Desk" on={perms.trading.otc} onToggle={() => set("trading.otc", !perms.trading.otc)} testid="perm-otc" />
        <PermToggle label="P2P Trading" on={perms.trading.p2p} onToggle={() => set("trading.p2p", !perms.trading.p2p)} testid="perm-p2p" />
      </PermSection>

      <PermSection title="Wallet Permissions">
        <PermToggle label="Deposits" on={perms.wallet.deposit} onToggle={() => set("wallet.deposit", !perms.wallet.deposit)} testid="perm-deposit" />
        <PermToggle label="Withdrawals" on={perms.wallet.withdraw} onToggle={() => set("wallet.withdraw", !perms.wallet.withdraw)} testid="perm-withdraw">
          <PermSubField label="Daily Limit">
            <input type="number" value={perms.wallet.withdraw_daily_limit_inr}
              onChange={(e) => set("wallet.withdraw_daily_limit_inr", Number(e.target.value))}
              data-testid="perm-withdraw-limit"
              className="h-[34px] px-2 w-[120px] rounded-md border bg-surface text-[12px] tabular-nums outline-none text-right"
              />
          </PermSubField>
        </PermToggle>
      </PermSection>

      <PermSection title="API Access">
        <PermToggle label="API Enabled" on={perms.api.enabled} onToggle={() => set("api.enabled", !perms.api.enabled)} testid="perm-api-enabled" />
        <PermToggle label="API Trading" on={perms.api.trading} onToggle={() => set("api.trading", !perms.api.trading)} testid="perm-api-trading" />
        <PermToggle label="API Withdrawal" on={perms.api.withdrawal} onToggle={() => set("api.withdrawal", !perms.api.withdrawal)} testid="perm-api-withdraw" />
      </PermSection>

      <PermSection title="Feature Access" cols={5} last>
        {[
          ["copy_trading", "Copy Trading"],
          ["high_leverage", "High Leverage"],
          ["new_listings", "New Listings"],
          ["margin", "Margin Trading"],
          ["p2p", "P2p"],
        ].map(([k, lbl]) => (
          <PermToggle key={k} label={lbl} on={perms.features[k]} onToggle={() => set(`features.${k}`, !perms.features[k])} testid={`perm-feat-${k}`} />
        ))}
      </PermSection>
    </Panel>
  );
}

function PermSection({ title, cols = 3, last, children }) {
  return (
    <div className={`${last ? "" : "pb-6 mb-6 border-b"}`}>
      <h4 className="text-[14px] font-semibold mb-4">{title}</h4>
      <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-${cols} gap-3`}>{children}</div>
    </div>
  );
}

function PermToggle({ label, on, onToggle, testid, children }) {
  return (
    <div className="rounded-lg border bg-background p-4">
      <div className="flex items-center justify-between">
        <span className="text-[14px] font-semibold">{label}</span>
        <button type="button" onClick={onToggle} data-testid={testid}
          className="w-12 h-7 rounded-full relative transition shrink-0"
          style={{ background: on ? "#44D15B" : "#1A2C34" }}>
          <span className="absolute top-1 w-5 h-5 bg-white rounded-full transition" style={{ left: on ? "26px" : "4px" }} />
        </button>
      </div>
      {children && <div className="mt-3">{children}</div>}
    </div>
  );
}

function PermSubField({ label, children }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-[12px] text-muted-foreground">{label}</span>
      {children}
    </div>
  );
}

function TradesTab({ userId }) {
  const [data, setData] = useState({ trades: [], has_liquidation_warning: false, total: 0 });
  const [filters, setFilters] = useState({ market: "all", type: "all", side: "all", liquidation: "all", date_from: "", date_to: "" });
  const reload = () => api.get(`/api/admin/users/${userId}/trades-v2`, { params: { ...filters, date_from: filters.date_from || undefined, date_to: filters.date_to || undefined } }).then(({ data }) => setData(data));
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [userId, filters.market, filters.type, filters.side, filters.liquidation, filters.date_from, filters.date_to]);

  const typePillBg = { spot: "rgba(59,130,246,0.2)", otc: "rgba(40, 202, 113,0.2)", p2p: "rgba(16,185,129,0.2)" };
  const typePillFg = { spot: "#3B82F6", otc: "#28CA71", p2p: "#44D15B" };

  function exportCsv() {
    const rows = [["Trade ID", "Order ID", "Market", "Type", "Side", "Price", "Quantity", "Fee", "P&L", "Timestamp"]];
    data.trades.forEach((t) => rows.push([t.trade_id, t.order_id, t.market, t.type, t.side, t.price, t.qty, t.fee, t.pnl ?? "", t.timestamp]));
    const csv = rows.map((r) => r.join(",")).join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `user-trades-${userId.slice(-6)}.csv`;
    a.click();
  }

  return (
    <Panel className="p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-[16px] font-semibold">Trade History</h3>
        <button onClick={exportCsv} data-testid="trades-export"
          className="inline-flex items-center gap-1.5 h-[36px] px-4 rounded-md border font-semibold text-[12px]"
         >
          <Download size={13} /> Export CSV
        </button>
      </div>

      {data.has_liquidation_warning && (
        <div data-testid="liquidation-warning"
          className="mb-4 px-4 py-3 rounded-lg border flex items-center gap-2 text-[13px]"
          style={{ borderColor: "rgba(239,68,68,0.4)", background: "rgba(239,68,68,0.08)", color: "#FCA5A5" }}>
          <AlertTriangle size={14} /> This user has liquidation trades in their history
        </div>
      )}

      <div className="rounded-lg border bg-background p-4 mb-4 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
        <FilterInput label="From Date" type="date" v={filters.date_from} on={(v) => setFilters({ ...filters, date_from: v })} testid="trades-from" />
        <FilterInput label="To Date" type="date" v={filters.date_to} on={(v) => setFilters({ ...filters, date_to: v })} testid="trades-to" />
        <FilterSelect label="Market" v={filters.market} on={(v) => setFilters({ ...filters, market: v })} testid="trades-market"
          options={[["all", "All Markets"], ["BTC/INR", "BTC/INR"], ["ETH/INR", "ETH/INR"], ["SOL/INR", "SOL/INR"], ["DOGE/INR", "DOGE/INR"]]} />
        <FilterSelect label="Type" v={filters.type} on={(v) => setFilters({ ...filters, type: v })} testid="trades-type"
          options={[["all", "All Types"], ["spot", "Spot"], ["otc", "OTC"], ["p2p", "P2P"]]} />
        <FilterSelect label="Side" v={filters.side} on={(v) => setFilters({ ...filters, side: v })} testid="trades-side"
          options={[["all", "All"], ["buy", "Buy"], ["sell", "Sell"]]} />
        <FilterSelect label="Liquidation" v={filters.liquidation} on={(v) => setFilters({ ...filters, liquidation: v })} testid="trades-liq"
          options={[["all", "All"], ["true", "Yes"], ["false", "No"]]} />
      </div>

      <div className="-mx-5 overflow-x-auto">
        <table className="w-full text-[13px]" data-testid="user-trades-table">
          <thead>
            <tr className="border-b text-[11px] uppercase tracking-wider" style={{ borderColor: "#1A2C34", color: "#899CA3" }}>
              {["Trade ID","Order ID","Market","Type","Side","Price","Quantity","Fee","P&L","Timestamp","Actions"].map((h, i) => (
                <th key={h} className={`px-4 py-3 font-semibold ${[5,6,7,8].includes(i) ? "text-right" : ""}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y">
            {data.trades.map((t) => (
              <tr key={t.trade_id} className="hover:bg-surface-2"
                style={t.liquidation ? { background: "rgba(239,68,68,0.06)" } : {}}>
                <td className="px-4 py-3 font-mono">{t.trade_id}</td>
                <td className="px-4 py-3 font-mono text-dim">{t.order_id}</td>
                <td className="px-4 py-3 font-semibold">{t.market}</td>
                <td className="px-4 py-3">
                  <span className="text-[10px] font-semibold uppercase px-2 py-1 rounded"
                    style={{ background: typePillBg[t.type], color: typePillFg[t.type] }}>{t.type}</span>
                </td>
                <td className="px-4 py-3 font-semibold" style={{ color: t.side === "buy" ? "#44D15B" : "#F4455D" }}>{t.side.toUpperCase()}</td>
                <td className="px-4 py-3 text-right tabular-nums">₹{Number(t.price).toLocaleString("en-IN")}</td>
                <td className="px-4 py-3 text-right tabular-nums">{t.qty}</td>
                <td className="px-4 py-3 text-right tabular-nums" style={{ color: "#899CA3" }}>₹{t.fee}</td>
                <td className="px-4 py-3 text-right tabular-nums font-semibold"
                  style={{ color: t.pnl == null ? "#5B6C73" : t.pnl >= 0 ? "#44D15B" : "#F4455D" }}>
                  {t.pnl == null ? "—" : `${t.pnl >= 0 ? "+" : ""}₹${Math.abs(t.pnl).toLocaleString("en-IN")}`}
                </td>
                <td className="px-4 py-3 text-muted-foreground text-[12px]">{new Date(t.timestamp).toISOString().slice(0, 19).replace("T", " ")}</td>
                <td className="px-4 py-3">
                  <button className="w-7 h-7 grid place-items-center rounded text-muted-foreground hover:text-primary"
                    data-testid={`trade-view-${t.trade_id}`} aria-label="View">
                    <Eye size={13} />
                  </button>
                </td>
              </tr>
            ))}
            {data.trades.length === 0 && (
              <tr><td colSpan={11} className="p-12 text-center text-muted-foreground">No trades match filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Panel>
  );
}

function FilterInput({ label, type = "text", v, on, testid }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider mb-1 text-muted-foreground">{label}</span>
      <input type={type} value={v} onChange={(e) => on(e.target.value)} data-testid={testid}
        className="w-full h-[36px] px-2 rounded-md border bg-surface text-[12px] outline-none"
        style={{ borderColor: "#1A2C34", color: "#E4EDEF", colorScheme: "dark" }} />
    </label>
  );
}
function FilterSelect({ label, v, on, options, testid }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-wider mb-1 text-muted-foreground">{label}</span>
      <div className="relative">
        <select value={v} onChange={(e) => on(e.target.value)} data-testid={testid}
          className="appearance-none w-full h-[36px] px-2 pr-7 rounded-md border bg-surface text-[12px] outline-none"
         >
          {options.map(([val, lbl]) => <option key={val} value={val}>{lbl}</option>)}
        </select>
        <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-dim" />
      </div>
    </label>
  );
}

function WalletsTab({ userId }) {
  const [data, setData] = useState(null);
  const [showDeposits, setShowDeposits] = useState(false);
  const [showWithdrawals, setShowWithdrawals] = useState(false);
  useEffect(() => {
    api.get(`/api/admin/users/${userId}/wallets`).then(({ data }) => setData(data));
  }, [userId]);
  if (!data) return <Panel className="p-5 text-muted-foreground">Loading…</Panel>;

  const fmt = (v, d) => Number(v).toLocaleString("en-IN", { minimumFractionDigits: d, maximumFractionDigits: d });

  return (
    <Panel className="p-5">
      <h3 className="text-[16px] font-semibold mb-5">Wallets &amp; Balances</h3>

      <div className="mb-4 px-4 py-3 rounded-lg border flex items-center gap-2 text-[13px]"
        style={{ borderColor: "rgba(40, 202, 113,0.4)", background: "rgba(40, 202, 113,0.08)", color: "#28CA71" }}>
        <Lock size={13} /> Read-only view. Manual credit/debit requires Finance/Wallet Ops module access.
      </div>

      <div className="rounded-lg border bg-background p-4">
        <h4 className="font-semibold text-[14px] mb-4">Wallet Overview</h4>
        <div className="-mx-4 overflow-x-auto">
          <table className="w-full text-[13px]" data-testid="user-wallets-table">
            <thead>
              <tr className="border-b text-[11px] uppercase tracking-wider" style={{ borderColor: "#1A2C34", color: "#899CA3" }}>
                {["Asset","Total Balance","Available","Locked","In-Order","Chains","Status","Actions"].map((h, i) => (
                  <th key={h} className={`px-4 py-3 font-semibold ${[1,2,3,4].includes(i) ? "text-right" : ""}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y">
              {data.balances.map((b) => (
                <tr key={b.asset} className="hover:bg-surface-2">
                  <td className="px-4 py-3 font-semibold text-[14px]">{b.asset}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-semibold">{b.asset === "INR" ? `₹${fmt(b.total, 0)}` : fmt(b.total, b.decimals)}</td>
                  <td className="px-4 py-3 text-right tabular-nums" style={{ color: "#44D15B" }}>{b.asset === "INR" ? `₹${fmt(b.available, 0)}` : fmt(b.available, b.decimals)}</td>
                  <td className="px-4 py-3 text-right tabular-nums" style={{ color: "#28CA71" }}>{b.asset === "INR" ? `₹${fmt(b.locked, 0)}` : fmt(b.locked, b.decimals)}</td>
                  <td className="px-4 py-3 text-right tabular-nums" style={{ color: "#899CA3" }}>{b.asset === "INR" ? `₹${fmt(b.in_order, 0)}` : fmt(b.in_order, b.decimals)}</td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap gap-1">
                      {b.chains.map((c) => (
                        <span key={c} className="text-[10px] font-semibold px-2 py-1 rounded"
                          style={{ background: "rgba(136,146,168,0.15)", color: "#899CA3" }}>{c}</span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-3"><StatusPill value={b.status} /></td>
                  <td className="px-4 py-3">
                    <div className="flex gap-1.5">
                      <button title="Freeze (read-only)" disabled
                        className="w-7 h-7 grid place-items-center rounded"
                        style={{ background: "rgba(239,68,68,0.18)", color: "#F4455D", border: "1px solid rgba(239,68,68,0.4)", opacity: 0.6 }}>
                        <Lock size={13} />
                      </button>
                      <button title="Statement"
                        className="w-7 h-7 grid place-items-center rounded"
                        style={{ background: "rgba(59,130,246,0.18)", color: "#3B82F6", border: "1px solid rgba(59,130,246,0.4)" }}>
                        <FileText size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <Collapsible title="Deposit History" open={showDeposits} onToggle={() => setShowDeposits(!showDeposits)} testid="wallet-deposits">
        <HistoryTable rows={data.deposits} kind="deposit" />
      </Collapsible>

      <Collapsible title="Withdrawal History" open={showWithdrawals} onToggle={() => setShowWithdrawals(!showWithdrawals)} testid="wallet-withdrawals">
        <HistoryTable rows={data.withdrawals} kind="withdrawal" />
      </Collapsible>
    </Panel>
  );
}

function Collapsible({ title, open, onToggle, testid, children }) {
  return (
    <div className="mt-4 rounded-lg border bg-background">
      <button onClick={onToggle} data-testid={testid}
        className="w-full flex items-center justify-between px-4 py-3 font-semibold text-[14px] text-left">
        <span>{title}</span>
        <ChevronDown size={14} className="text-muted-foreground transition-transform"
          style={{ transform: open ? "rotate(180deg)" : "" }} />
      </button>
      {open && <div className="px-4 pb-4">{children}</div>}
    </div>
  );
}

function HistoryTable({ rows }) {
  if (!rows || rows.length === 0) return <div className="text-[12px] text-muted-foreground py-3">No history.</div>;
  return (
    <div className="-mx-4 overflow-x-auto">
      <table className="w-full text-[12px]">
        <thead>
          <tr className="border-b text-[10px] uppercase tracking-wider" style={{ borderColor: "#1A2C34", color: "#899CA3" }}>
            {["ID","Asset","Chain","Amount","Status","Tx Hash","When"].map((h) => (
              <th key={h} className="px-3 py-2 font-semibold text-left">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y">
          {rows.map((r) => (
            <tr key={r.id}>
              <td className="px-3 py-2 font-mono">{r.id}</td>
              <td className="px-3 py-2 font-semibold">{r.asset}</td>
              <td className="px-3 py-2 text-muted-foreground">{r.chain}</td>
              <td className="px-3 py-2 font-semibold tabular-nums">{r.amount.toLocaleString("en-IN")}</td>
              <td className="px-3 py-2"><StatusPill value={r.status} /></td>
              <td className="px-3 py-2 font-mono text-[10px] text-muted-foreground truncate max-w-[180px]">{r.tx_hash}</td>
              <td className="px-3 py-2 text-muted-foreground">{new Date(r.created_at).toLocaleString()}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RiskTab({ user }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        <Panel className="p-5">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Risk Score</div>
          <div className="text-[32px] font-semibold text-up">25</div>
          <div className="text-[11px] text-muted-foreground mt-1">Low risk profile</div>
        </Panel>
        <Panel className="p-5">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Max Leverage</div>
          <div className="text-[32px] font-semibold">10x</div>
          <div className="text-[11px] text-muted-foreground mt-1">Account default</div>
        </Panel>
        <Panel className="p-5">
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Open Exposure</div>
          <div className="text-[32px] font-semibold tabular-nums">₹0</div>
          <div className="text-[11px] text-muted-foreground mt-1">No open positions</div>
        </Panel>
      </div>
    </div>
  );
}

function ActivityTab({ history }) {
  return (
    <Panel>
      <div className="p-4 border-b border-line"><div className="text-[14px] font-semibold">Login & Activity Audit</div></div>
      <DataTable
        columns={[
          { key: "created_at", header: "When", render: (a) => <span className="text-muted-foreground text-[11px]">{new Date(a.created_at).toLocaleString()}</span> },
          { key: "event", header: "Event" },
          { key: "ok", header: "Result", render: (a) => <StatusPill value={a.ok ? "ok" : "failed"} /> },
          { key: "ip", header: "IP", render: (a) => <span className="text-dim text-[11px]">{a.ip || "—"}</span> },
        ]}
        rows={history}
        empty="No login history."
      />
    </Panel>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-center justify-between p-3 bg-background border border-line rounded-md">
      <div className="flex items-center gap-2 text-[13px]"><Icon size={14} className="text-primary" /> {label}</div>
      <div className="text-[12px] font-semibold text-muted-foreground">{value}</div>
    </div>
  );
}

function NoteModal({ userId, onClose, onSaved }) {
  const [body, setBody] = useState("");
  const [saving, setSaving] = useState(false);
  async function save() {
    if (!body.trim()) return;
    setSaving(true);
    await api.post(`/api/admin/users/${userId}/notes`, { body });
    onSaved();
  }
  return (
    <div className="fixed inset-0 z-[100] bg-black/60 grid place-items-center p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="bg-surface border border-line rounded-xl w-full max-w-[480px] p-5">
        <h3 className="text-[16px] font-semibold mb-3">Add internal note</h3>
        <textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Note for compliance / support team..." data-testid="note-body" className="w-full p-3 bg-background border border-line rounded-md text-[13px]" />
        <div className="mt-3 flex justify-end gap-2">
          <Btn kind="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={save} disabled={saving} data-testid="note-save"><Save size={13} /> Save note</Btn>
        </div>
      </div>
    </div>
  );
}
